// "Pick up where you left off" on the home page (returning-visits goal, owner 2026-10-10). The games already save
// themselves; this keeps a short list of the pages where a game was started and not finished, so the home page can
// link straight back to them. Kept on the device only (localStorage), like the saves themselves.
import { localISO, status } from "./daily"

export interface Recent { path: string; name: string; at: string; done: boolean; date?: string }

const KEY = "dt:recent"
const MAX = 3

export function read(): Recent[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]") as unknown
    return Array.isArray(v) ? (v as Recent[]).filter((r) => r && typeof r.path === "string" && typeof r.name === "string") : []
  } catch { return [] }
}

function write(list: Recent[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))) } catch { /* storage blocked: nothing to show later */ }
}

/** Today's daily page (/x/daily) changes at midnight, so its entry only counts on the day it was played. */
const isTodayDaily = (path: string): boolean => /\/daily$/.test(path)

/** A game was started on this page (first move) or ended (won or lost: nothing left to pick up). */
export function note(path: string, name: string, kind: "start" | "end", today = localISO()): void {
  const rest = read().filter((r) => r.path !== path)
  const entry: Recent = { path, name, at: today, done: kind === "end", ...(isTodayDaily(path) ? { date: today } : {}) }
  write([ entry, ...rest ])
}

/** Unfinished games worth a link: not done, and not yesterday's daily. */
export function pending(list: Recent[], today = localISO()): Recent[] {
  return list.filter((r) => !r.done && (!r.date || r.date === today))
}

const when = (at: string, today: string): string => {
  if (at === today) return "today"
  const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${at}T00:00:00Z`)) / 86_400_000)
  return days === 1 ? "yesterday" : `${days} days ago`
}

/** Fills the home page's hidden section, or leaves it hidden when there's nothing to say. */
export function renderContinue(section: HTMLElement, today = localISO()): void {
  const games = (section.dataset.dailyGames ?? "").split(",").filter(Boolean)
  const solved = games.filter((g) => status(g, today) === "won").length
  const started = games.filter((g) => status(g, today) === "played").length
  const items = pending(read(), today)
  const list = section.querySelector<HTMLElement>("[data-continue-list]")
  const daily = section.querySelector<HTMLElement>("[data-continue-daily]")
  if (!list || !daily) return
  if (solved || started) {
    daily.textContent = solved === games.length
      ? `You've solved all ${solved} of today's puzzles. New ones arrive at midnight.`
      : `Today: ${solved} of ${games.length} daily puzzles solved${started ? `, ${started} started` : ""}.`
    const link = document.createElement("a")
    link.href = "/today"
    link.textContent = solved === games.length ? "See your calendar" : "Carry on with today's puzzles"
    daily.append(" ", link)
    daily.hidden = false
  }
  list.replaceChildren(...items.map((r) => {
    const li = document.createElement("li")
    const a = document.createElement("a")
    a.href = r.path
    a.textContent = r.name
    li.append(a, ` · started ${when(r.at, today)}`)
    return li
  }))
  list.hidden = items.length === 0
  section.hidden = daily.hidden && list.hidden
}
